import React, { useState, useEffect } from "react";
import {
  getAllPlayers,
  createPlayer,
  updatePlayer,
  deletePlayer,
  getAllTeams,
  transferPlayer,
  getMatchesForExport,
} from "../../Libs/api";
import Loader from "../../Component/Loader";
import {
  Search,
  Plus,
  User,
  Trash2,
  Edit,
  ArrowRight,
  Download,
} from "lucide-react";
import Swal from "sweetalert2";

const PlayerManagement = () => {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState({
    playerName: "",
    phoneNumber: "",
    teamId: "",
    photo: "",
    skillLevel: "Beginner",
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [filterTeam, setFilterTeam] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [shouldRenderModal, setShouldRenderModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [playersResponse, teamsResponse] = await Promise.all([
        getAllPlayers(),
        getAllTeams(),
      ]);

      if (playersResponse.success) {
        setPlayers(playersResponse.data);
      } else {
        setError("Failed to fetch players");
      }

      if (teamsResponse.success) {
        setTeams(teamsResponse.data);
      } else {
        setError("Failed to fetch teams");
      }
    } catch (error) {
      setError("Error fetching data: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        Swal.fire("Error!", "Please select a valid image file", "error");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire("Error!", "Image size should be less than 5MB", "error");
        return;
      }

      setPhotoFile(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      if (!formData.playerName.trim() || !formData.phoneNumber) {
        Swal.fire(
          "Error!",
          "Player name and phone number are required",
          "error"
        );
        return;
      }

      // Validate phone number format (basic validation)
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(formData.phoneNumber.replace(/[\s\-\(\)]/g, ""))) {
        Swal.fire("Error!", "Please enter a valid phone number", "error");
        return;
      }

      setModalLoading(true);

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("playerName", formData.playerName);
      submitData.append("phoneNumber", formData.phoneNumber);
      submitData.append("teamId", formData.teamId || "");
      submitData.append("skillLevel", formData.skillLevel);

      // Only append photo if it's a new file or editing without existing photo
      if (photoFile) {
        submitData.append("photo", photoFile);
      } else if (editingPlayer && formData.photo) {
        // Keep existing photo URL if no new file is selected
        submitData.append("photo", formData.photo);
      }

      let response;
      if (editingPlayer) {
        response = await updatePlayer(editingPlayer.playerId, submitData);
      } else {
        response = await createPlayer(submitData);
      }

      if (response.success) {
        Swal.fire(
          "Success!",
          editingPlayer
            ? "Player updated successfully"
            : "Player created successfully",
          "success"
        );
        handleCloseAddModal();
        fetchData();
      } else {
        Swal.fire("Error!", response.message || "Operation failed", "error");
      }
    } catch (error) {
      Swal.fire("Error!", "Error: " + error.message, "error");
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = (player) => {
    setEditingPlayer(player);
    setFormData({
      playerName: player.playerName,
      phoneNumber: player.phoneNumber || "",
      teamId: player.teamId || "",
      photo: player.photo || "",
      skillLevel: player.skillLevel || "Beginner",
    });
    setPhotoFile(null);
    setPhotoPreview(player.photo || "");
    handleOpenModal();
  };

  const handleDelete = async (playerId, playerName) => {
    const result = await Swal.fire({
      title: "Delete Player",
      text: `Are you sure you want to delete ${playerName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Yes, delete!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const response = await deletePlayer(playerId);
        if (response.success) {
          Swal.fire(
            "Deleted!",
            "Player has been deleted successfully.",
            "success"
          );
          fetchData();
        } else {
          Swal.fire(
            "Error!",
            response.message || "Failed to delete player",
            "error"
          );
        }
      } catch (error) {
        Swal.fire("Error!", "Error deleting player: " + error.message, "error");
      }
    }
  };

  const handleTransfer = async (playerId, newTeamId) => {
    try {
      const response = await transferPlayer(playerId, newTeamId);
      if (response.success) {
        Swal.fire("Success!", "Player transferred successfully", "success");
        fetchData();
      } else {
        Swal.fire(
          "Error!",
          response.message || "Failed to transfer player",
          "error"
        );
      }
    } catch (error) {
      Swal.fire(
        "Error!",
        "Error transferring player: " + error.message,
        "error"
      );
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPlayer(null);
    setFormData({
      playerName: "",
      phoneNumber: "",
      teamId: "",
      photo: "",
      skillLevel: "Beginner",
    });
    setError("");
    setSuccess("");
  };

  // Modal functions
  const handleOpenModal = () => {
    setShouldRenderModal(true);
    setTimeout(() => setIsModalVisible(true), 5);
  };

  const handleCloseAddModal = () => {
    setIsModalVisible(false);
    setTimeout(() => {
      setShouldRenderModal(false);
      setFormData({
        playerName: "",
        phoneNumber: "",
        teamId: "",
        photo: "",
        skillLevel: "Beginner",
      });
      setPhotoFile(null);
      setPhotoPreview("");
      setEditingPlayer(null);
    }, 400);
  };

  // Filter players based on search query and team filter
  const filteredPlayers = players.filter((player) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      player.playerName.toLowerCase().includes(searchLower) ||
      (player.phoneNumber && player.phoneNumber.includes(searchQuery)) ||
      (player.team && player.team.teamName.toLowerCase().includes(searchLower));

    const matchesTeam = filterTeam
      ? filterTeam === "no-team"
        ? !player.teamId
        : player.teamId === parseInt(filterTeam)
      : true;

    return matchesSearch && matchesTeam;
  });

  // Convert match data to CSV format
  const convertMatchesToCSV = (matches) => {
    // Create 9 empty rows
    const emptyRows = Array(9)
      .fill("")
      .map(() => Array(30).fill(""));

    // Header row
    const headers = [
      "",
      "",
      "",
      "matchType",
      "event",
      "date",
      "playerA1",
      "playerA1DuprId",
      "playerA1ExternalId",
      "playerA2",
      "playerA2DuprId",
      "playerA2ExternalId",
      "playerB1",
      "playerB1DuprId",
      "playerB1ExternalId",
      "playerB2",
      "playerB2DuprId",
      "playerB2ExternalId",
      "",
      "teamAGame1",
      "teamBGame1",
      "teamAGame2",
      "teamBGame2",
      "teamAGame3",
      "teamBGame3",
      "teamAGame4",
      "teamBGame4",
      "teamAGame5",
      "teamBGame5",
    ];

    // Convert matches to CSV rows
    const dataRows = matches.map((match) => {
      // Determine match type (S for Singles, D for Doubles)
      const isSingles = !match.teamAPlayer2Id && !match.teamBPlayer2Id;
      const matchType = isSingles ? "S" : "D";

      // Format date (YYYY-MM-DD)
      const matchDate = new Date(match.createdAt).toISOString().split("T")[0];

      // Get player names (legacy CSV columns still labeled DuprId; use playerCode)
      const playerA1Name = match.teamAPlayer1?.playerName || "";
      const playerA1DuprId = match.teamAPlayer1?.playerCode || "";
      const playerA2Name = match.teamAPlayer2?.playerName || "";
      const playerA2DuprId = match.teamAPlayer2?.playerCode || "";
      const playerB1Name = match.teamBPlayer1?.playerName || "";
      const playerB1DuprId = match.teamBPlayer1?.playerCode || "";
      const playerB2Name = match.teamBPlayer2?.playerName || "";
      const playerB2DuprId = match.teamBPlayer2?.playerCode || "";

      // Get game scores (sets)
      const sets = match.sets || [];
      const gameScores = [];
      for (let i = 0; i < 5; i++) {
        if (sets[i] && sets[i].isCompleted) {
          gameScores.push(sets[i].teamAScore || "");
          gameScores.push(sets[i].teamBScore || "");
        } else {
          gameScores.push("");
          gameScores.push("");
        }
      }

      return [
        "",
        "",
        "",
        matchType,
        "3rd Shot Premier League 1.0",
        matchDate,
        playerA1Name,
        playerA1DuprId,
        "", // External ID (empty)
        playerA2Name,
        playerA2DuprId,
        "", // External ID (empty)
        playerB1Name,
        playerB1DuprId,
        "", // External ID (empty)
        playerB2Name,
        playerB2DuprId,
        "", // External ID (empty)
        "",
        ...gameScores,
      ];
    });

    // Combine all rows
    const allRows = [...dataRows];

    // Convert to CSV string
    return allRows
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const response = await getMatchesForExport();

      if (response.success && response.data) {
        const csvContent = convertMatchesToCSV(response.data);

        // Create blob and download
        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `club_matches_${new Date().toISOString().split("T")[0]}.csv`
        );
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        Swal.fire("Success!", "CSV file downloaded successfully", "success");
      } else {
        Swal.fire(
          "Error!",
          response.message || "Failed to export matches",
          "error"
        );
      }
    } catch (error) {
      Swal.fire("Error!", "Error exporting matches: " + error.message, "error");
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <div
        style={{
          minHeight: "100vh",
          padding: "0",
          margin: "0",
          color: "white",
          paddingBottom: 80,
          overflowY: "auto",
          maxHeight: "100vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            backgroundColor: "#001837",
            color: "white",
            padding: "16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingBottom: 20,
          }}
        >
          <div>
            <h2
              style={{
                fontSize: "24px",
                fontWeight: "900",
                color: "white",
                margin: "0 0 8px 0",
              }}
            >
              Player Management
            </h2>
            <p
              style={{
                fontSize: "14px",
                color: "#E5E7EB",
                margin: "0",
              }}
            >
              Manage players and their team assignments
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "16px",
            borderBottom: "1px solid #e9ecef",
          }}
        >
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <Search
              size={20}
              style={{
                position: "absolute",
                left: "12px",
                color: "#6c757d",
              }}
            />
            <input
              type="text"
              placeholder="Search players by name, phone number, or team..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 12px 12px 44px",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                backgroundColor: "white",
              }}
            />
          </div>

          {/* Team Filter */}
          <select
            value={filterTeam}
            onChange={(e) => setFilterTeam(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 16px",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "white",
              color: "#374151",
            }}
          >
            <option value="">All Players</option>
            <option value="no-team">Players without Team</option>
            {teams.map((team) => (
              <option key={team.teamId} value={team.teamId}>
                {team.teamName}
              </option>
            ))}
          </select>
        </div>

        {/* Add Player Button and Export Button */}
        <div
          style={{
            backgroundColor: "white",
            padding: "16px",
            borderBottom: "1px solid #e9ecef",
            display: "flex",
            gap: "12px",
          }}
        >
          <button
            onClick={handleOpenModal}
            style={{
              flex: 1,
              backgroundColor: "#001837",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "all 0.2s ease-in-out",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#002855";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#001837";
            }}
          >
            <Plus size={16} />
            Add New Player
          </button>
          <button
            onClick={handleExportCSV}
            disabled={exportLoading}
            style={{
              flex: 1,
              backgroundColor: exportLoading ? "#9CA3AF" : "#028F64",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: exportLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "all 0.2s ease-in-out",
            }}
            onMouseEnter={(e) => {
              if (!exportLoading) {
                e.target.style.backgroundColor = "#027A56";
              }
            }}
            onMouseLeave={(e) => {
              if (!exportLoading) {
                e.target.style.backgroundColor = "#028F64";
              }
            }}
          >
            <Download size={16} />
            {exportLoading ? "Exporting..." : "Export Data"}
          </button>
        </div>

        {/* Players List */}
        <div
          style={{
            backgroundColor: "#f8fafc",
            minHeight: "calc(100vh - 300px)",
            padding: "16px 0",
          }}
        >
          {filteredPlayers.length === 0 ? (
            <div
              style={{
                padding: "60px 20px",
                textAlign: "center",
                color: "#64748b",
                backgroundColor: "white",
                margin: "16px 12px",
                borderRadius: "12px",
                boxShadow:
                  "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
              }}
            >
              <div
                style={{
                  fontSize: "48px",
                  marginBottom: "16px",
                }}
              >
                👥
              </div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1e293b",
                  margin: "0 0 8px 0",
                }}
              >
                {searchQuery || filterTeam
                  ? "No players found"
                  : "No players available"}
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                }}
              >
                {searchQuery || filterTeam
                  ? "Try adjusting your search criteria or filters."
                  : "Add your first player to get started."}
              </p>
            </div>
          ) : (
            filteredPlayers.map((player, idx) => (
              <div
                key={player.playerId}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  borderBottom: "1px solid #eee",
                  padding: "16px 12px",
                  backgroundColor: "white",
                }}
              >
                {/* Main Content */}
                <div style={{ position: "relative", zIndex: 1, width: "100%" }}>
                  {/* Header Section */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "20px",
                    }}
                  >
                    {/* Left: Photo and Basic Info */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "20px",
                        flex: 1,
                      }}
                    >
                      {/* Player Photo */}
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#1a2332",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {player.photo ? (
                            <img
                              src={player.photo}
                              alt={player.playerName}
                              style={{
                                width: "52px",
                                height: "62px",
                                borderRadius: "8px",
                                objectFit: "fill",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                backgroundColor: "#001837",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <User size={16} color="white" />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Player Name and Number */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "4px",
                            marginBottom: "8px",
                            flexDirection: "column",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 600,
                              fontSize: 12,
                              color: "#64748b",
                              backgroundColor: "#f1f5f9",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              minWidth: "32px",
                              textAlign: "center",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            # {player.playerCode || "N/A"}
                          </span>

                          <h3
                            style={{
                              fontWeight: 800,
                              fontSize: 24,
                              color: "#1e293b",
                              margin: 0,
                              lineHeight: 1.2,
                              letterSpacing: "-0.02em",
                            }}
                          >
                            {player.playerName}
                          </h3>
                        </div>
                      </div>
                    </div>

                    {/* Right: Action Buttons */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      {/* Edit Button */}
                      <button
                        onClick={() => handleEdit(player)}
                        style={{
                          backgroundColor: "#F0F9FF",
                          border: "1px solid #BAE6FD",
                          borderRadius: "8px",
                          padding: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                          minWidth: "32px",
                          height: "32px",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#E0F2FE";
                          e.target.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#F0F9FF";
                          e.target.style.transform = "scale(1)";
                        }}
                      >
                        <Edit size={14} color="#0284C7" />
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() =>
                          handleDelete(player.playerId, player.playerName)
                        }
                        style={{
                          backgroundColor: "#FEF2F2",
                          border: "1px solid #FECACA",
                          borderRadius: "8px",
                          padding: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                          minWidth: "32px",
                          height: "32px",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#FEE2E2";
                          e.target.style.transform = "scale(1.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#FEF2F2";
                          e.target.style.transform = "scale(1)";
                        }}
                      >
                        <Trash2 size={14} color="#EF4444" />
                      </button>
                    </div>
                  </div>

                  {/* Bottom Section: Phone, Team, and Skill Level Info */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "12px",
                      paddingTop: "20px",
                      borderTop: "2px solid #f1f5f9",
                    }}
                  >
                    {/* Phone Number */}
                    <div
                      style={{
                        backgroundColor: "#f8f9fa",
                        color: "#666",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        fontSize: "14px",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        flex: 1,
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <span style={{ fontSize: "16px" }}>📞</span>
                      <span>{player.phoneNumber || "No Phone"}</span>
                    </div>

                    {/* Skill Level Badge */}
                    <div
                      style={{
                        backgroundColor:
                          player.skillLevel === "Advanced"
                            ? "#dbeafe"
                            : player.skillLevel === "Intermediate"
                            ? "#fef3c7"
                            : "#f3e8ff",
                        color:
                          player.skillLevel === "Advanced"
                            ? "#1e40af"
                            : player.skillLevel === "Intermediate"
                            ? "#92400e"
                            : "#6b21a8",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        fontSize: "14px",
                        fontWeight: "500",
                        border:
                          player.skillLevel === "Advanced"
                            ? "1px solid #93c5fd"
                            : player.skillLevel === "Intermediate"
                            ? "1px solid #fde68a"
                            : "1px solid #c4b5fd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "100px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "16px",
                          marginRight: "6px",
                        }}
                      >
                        {player.skillLevel === "Advanced"
                          ? "⭐"
                          : player.skillLevel === "Intermediate"
                          ? "🔥"
                          : "🌱"}
                      </span>
                      <span>{player.skillLevel || "Beginner"}</span>
                    </div>

                    {/* Team Badge */}
                    <div
                      style={{
                        backgroundColor: player.team ? "#d1fae5" : "#fee2e2",
                        color: player.team ? "#065f46" : "#991b1b",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        fontSize: "14px",
                        fontWeight: "500",
                        border: player.team
                          ? "1px solid #a7f3d0"
                          : "1px solid #fecaca",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "120px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "16px",
                          marginRight: "6px",
                        }}
                      >
                        {player.team ? "🏆" : "❌"}
                      </span>
                      <span>
                        {player.team ? player.team.teamName : "No Team"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add Player Modal */}
      {shouldRenderModal && (
        <div
          className={`offcanvas offcanvas-bottom ${
            isModalVisible ? "show" : ""
          }`}
          tabIndex="-1"
          id="addPlayerModal"
          aria-labelledby="addPlayerModalLabel"
          aria-modal="true"
          role="dialog"
          style={{
            minHeight: "100%",
            background: "transparent",
            transition:
              "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: isModalVisible ? "translateY(0)" : "translateY(100%)",
            opacity: isModalVisible ? 1 : 0,
          }}
        >
          <div
            className="offcanvas-header"
            style={{ background: "transparent", justifyContent: "flex-end" }}
          >
            <svg
              onClick={handleCloseAddModal}
              width="36"
              height="36"
              viewBox="0 0 36 36"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                cursor: "pointer",
                transition: "transform 0.2s ease-in-out",
              }}
              onMouseEnter={(e) => (e.target.style.transform = "scale(1.1)")}
              onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
            >
              <circle
                cx="18"
                cy="18"
                r="18"
                fill="white"
                style={{ fill: "white", fillOpacity: 1 }}
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M23.078 13.0001L12.998 23.0801L23.078 13.0001Z"
                fill="black"
                style={{ fill: "black", fillOpacity: 1 }}
              />
              <path
                d="M23.078 13.0001L12.998 23.0801"
                stroke="black"
                style={{ stroke: "black", strokeOpacity: 1 }}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M23.078 23.0801L12.998 13.0001L23.078 23.0801Z"
                fill="black"
                style={{ fill: "black", fillOpacity: 1 }}
              />
              <path
                d="M23.078 23.0801L12.998 13.0001"
                stroke="black"
                style={{ stroke: "black", strokeOpacity: 1 }}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div
            className="offcanvas-body border-top mx-0 flex-grow-0"
            style={{
              background: "white",
              height: "100%",
              padding: 0,
              transition:
                "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              transform: isModalVisible ? "translateY(0)" : "translateY(20px)",
              opacity: isModalVisible ? 1 : 0,
            }}
          >
            <div
              style={{
                background: "white",
                zIndex: 99999,
                padding: "17px 0px 0px 0px",
                position: "sticky",
                top: 0,
              }}
            >
              <div
                style={{
                  background: "#001837",
                  color: "white",
                  borderRadius: 16,
                  margin: "0 16px",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "white",
                      borderRadius: "50%",
                      width: "40px",
                      height: "40px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <User size={20} color="#001837" />
                  </div>
                  <div>
                    <h3
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "white",
                        margin: "0",
                      }}
                    >
                      {editingPlayer ? "Edit Player" : "Add New Player"}
                    </h3>
                    <p
                      style={{
                        fontSize: "14px",
                        color: "#E5E7EB",
                        margin: "0",
                      }}
                    >
                      {editingPlayer
                        ? "Update player information"
                        : "Create a new player with team assignment"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div style={{ backgroundColor: "white", padding: "20px 16px" }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
              >
                {/* Player Name Input */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Player Name *
                  </label>
                  <input
                    type="text"
                    value={formData.playerName}
                    onChange={handleInputChange}
                    name="playerName"
                    placeholder="Enter player name"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      color: "#374151",
                    }}
                    required
                  />
                </div>

                {/* Phone Number Input */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    name="phoneNumber"
                    placeholder="Enter phone number (e.g., +1234567890)"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      color: "#374151",
                    }}
                    required
                  />
                </div>

                {/* Team Selection */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Team
                  </label>
                  <select
                    value={formData.teamId}
                    onChange={handleInputChange}
                    name="teamId"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      color: "#374151",
                    }}
                  >
                    <option value="">Select Team (Optional)</option>
                    {teams.map((team) => (
                      <option key={team.teamId} value={team.teamId}>
                        {team.teamName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Skill Level Selection */}
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Skill Level
                  </label>
                  <select
                    value={formData.skillLevel}
                    onChange={handleInputChange}
                    name="skillLevel"
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "1px solid #D1D5DB",
                      borderRadius: "8px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      color: "#374151",
                    }}
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>

                {/* Photo Upload */}
                <div style={{ marginBottom: "24px" }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#374151",
                      marginBottom: "8px",
                    }}
                  >
                    Player Photo
                  </label>

                  {/* File Upload Area */}
                  <div
                    style={{
                      border: "2px dashed #D1D5DB",
                      borderRadius: "8px",
                      padding: "20px",
                      textAlign: "center",
                      backgroundColor: "#F9FAFB",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() =>
                      document.getElementById("photo-upload").click()
                    }
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = "#001837";
                      e.target.style.backgroundColor = "#F0F9FF";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = "#D1D5DB";
                      e.target.style.backgroundColor = "#F9FAFB";
                    }}
                  >
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      style={{ display: "none" }}
                    />

                    {photoPreview ? (
                      <div>
                        <img
                          src={photoPreview}
                          alt="Photo preview"
                          style={{
                            width: "80px",
                            height: "80px",
                            borderRadius: "50%",
                            objectFit: "cover",
                            marginBottom: "8px",
                          }}
                        />
                        <p
                          style={{
                            margin: "0",
                            fontSize: "14px",
                            color: "#6B7280",
                          }}
                        >
                          Click to change photo
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div
                          style={{
                            width: "48px",
                            height: "48px",
                            backgroundColor: "#E5E7EB",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 12px",
                          }}
                        >
                          <User size={24} color="#6B7280" />
                        </div>
                        <p
                          style={{
                            margin: "0 0 4px",
                            fontSize: "14px",
                            color: "#374151",
                          }}
                        >
                          Upload Player Photo
                        </p>
                        <p
                          style={{
                            margin: "0",
                            fontSize: "12px",
                            color: "#6B7280",
                          }}
                        >
                          PNG, JPG up to 5MB
                        </p>
                      </div>
                    )}
                  </div>

                  {photoFile && (
                    <p
                      style={{
                        margin: "8px 0 0",
                        fontSize: "12px",
                        color: "#059669",
                      }}
                    >
                      ✓ {photoFile.name} selected
                    </p>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={modalLoading}
                  style={{
                    width: "100%",
                    backgroundColor: modalLoading ? "#9CA3AF" : "#001837",
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    padding: "16px",
                    fontSize: "16px",
                    fontWeight: "600",
                    cursor: modalLoading ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    if (!modalLoading) {
                      e.target.style.backgroundColor = "#002855";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!modalLoading) {
                      e.target.style.backgroundColor = "#001837";
                    }
                  }}
                >
                  {modalLoading ? (
                    <>
                      <Loader />
                      {editingPlayer
                        ? "Updating Player..."
                        : "Creating Player..."}
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      {editingPlayer ? "Update Player" : "Create Player"}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {shouldRenderModal && (
        <div
          className={`offcanvas-backdrop fade ${isModalVisible ? "show" : ""}`}
          style={{
            transition: "opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            opacity: isModalVisible ? 0.5 : 0,
          }}
        ></div>
      )}
    </div>
  );
};

export default PlayerManagement;
